import React, { useEffect, useState } from 'react';
import { useTitle } from '../hooks/useTitle';
import { ArrowRight, Users, DollarSign, Target, CheckCircle2, MessageCircle, Briefcase } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

export function AffiliateProgram() {
  const { setTitle } = useTitle();
  const [loading, setLoading] = useState(false);
  const [partnerStatus, setPartnerStatus] = useState<any>(null);
  const [referrals, setReferrals] = useState<any[]>([]);
  const [stats, setStats] = useState<{
    totalReferrals: number;
    qualifiedLeads: number;
    earnings: number;
  }>({
    totalReferrals: 0,
    qualifiedLeads: 0,
    earnings: 0
  });

  useEffect(() => {
    setTitle('Programme de Recommandation - Novster AI');
    checkPartnerStatus();
  }, [setTitle]);

  const checkPartnerStatus = async () => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      const { data: partner } = await supabase
        .from('referral_partners')
        .select('*')
        .eq('user_id', user.user.id)
        .single();

      setPartnerStatus(partner);
      if (partner) {
        await loadReferrals(partner.id);
      }
    } catch (err) {
      console.error('Error checking partner status:', err);
    }
  };

  const loadReferrals = async (partnerId: string) => {
    try {
      const { data: referrals } = await supabase
        .from('referrals')
        .select(`
          *,
          referral_commissions (*)
        `)
        .eq('partner_id', partnerId);

      setReferrals(referrals || []);
      
      if (referrals) {
        const qualifiedLeads = referrals.filter(r => r.status === 'qualified' || r.status === 'converted').length;
        const totalEarnings = referrals.reduce((sum, r) => {
          return sum + (r.referral_commissions?.[0]?.commission_amount || 0);
        }, 0);

        setStats({
          totalReferrals: referrals.length,
          qualifiedLeads,
          earnings: totalEarnings
        });
      }
    } catch (err) {
      console.error('Error loading referrals:', err);
    }
  };

  const handleApply = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const formData = new FormData(e.currentTarget);
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Non authentifié');

      const { error } = await supabase
        .from('referral_partners')
        .insert([{
          user_id: user.user.id,
          name: formData.get('name'),
          email: formData.get('email'),
          phone: formData.get('phone'),
          company: formData.get('company'),
          commission_rate: 0.10
        }]);

      if (error) throw error;
      
      toast.success('Demande envoyée avec succès');
      checkPartnerStatus();
    } catch (err) {
      console.error('Error applying:', err);
      toast.error('Erreur lors de l\'envoi de la demande');
    } finally {
      setLoading(false);
    }
  };

  const handleAddReferral = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const formData = new FormData(e.currentTarget);
      
      const { error } = await supabase
        .from('referrals')
        .insert([{
          partner_id: partnerStatus.id,
          prospect_name: formData.get('prospect_name'),
          prospect_email: formData.get('prospect_email'),
          prospect_company: formData.get('prospect_company'),
          prospect_phone: formData.get('prospect_phone'),
          notes: formData.get('notes')
        }]);

      if (error) throw error;
      
      toast.success('Recommandation ajoutée avec succès');
      loadReferrals(partnerStatus.id);
      (e.target as HTMLFormElement).reset();
    } catch (err) {
      console.error('Error adding referral:', err);
      toast.error('Erreur lors de l\'ajout de la recommandation');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'contacted': return 'bg-blue-100 text-blue-800';
      case 'qualified': return 'bg-green-100 text-green-800';
      case 'converted': return 'bg-orange-100 text-orange-800';
      case 'lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'contacted': return 'Contacté';
      case 'qualified': return 'Qualifié';
      case 'converted': return 'Converti';
      case 'lost': return 'Perdu';
      default: return status;
    }
  };

  if (loading && !partnerStatus) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-gray-600">Chargement...</div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {!partnerStatus ? (
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4">Programme de Recommandation Novster AI</h1>
              <p className="text-xl text-gray-600">
                Gagnez jusqu'à 10% de commission en nous recommandant des clients qualifiés
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-orange-500 mb-4">
                  <DollarSign className="w-12 h-12" />
                </div>
                <h3 className="text-xl font-bold mb-2">10% de Commission</h3>
                <p className="text-gray-600">
                  Sur chaque contrat signé grâce à vos recommandations
                </p>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-orange-500 mb-4">
                  <Target className="w-12 h-12" />
                </div>
                <h3 className="text-xl font-bold mb-2">Suivi Personnalisé</h3>
                <p className="text-gray-600">
                  Nous prenons en charge tout le processus de vente
                </p>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-orange-500 mb-4">
                  <Users className="w-12 h-12" />
                </div>
                <h3 className="text-xl font-bold mb-2">Partenariat Durable</h3>
                <p className="text-gray-600">
                  Développez votre réseau et vos revenus
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-6">Devenir Partenaire</h2>
              <form onSubmit={handleApply} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email professionnel
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Entreprise
                  </label>
                  <input
                    type="text"
                    name="company"
                    required
                    className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-orange-600 transition-colors"
                >
                  {loading ? 'Envoi...' : 'Devenir partenaire'}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      ) : (
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-6xl mx-auto">
            <div className="mb-12">
              <h1 className="text-3xl font-bold mb-4">Tableau de bord Partenaire</h1>
              <p className="text-gray-600">
                Bienvenue {partnerStatus.name} ! Gérez vos recommandations et suivez vos commissions.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold">Recommandations</h3>
                  <Users className="w-6 h-6 text-orange-500" />
                </div>
                <p className="text-3xl font-bold">{stats.totalReferrals}</p>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold">Leads Qualifiés</h3>
                  <CheckCircle2 className="w-6 h-6 text-orange-500" />
                </div>
                <p className="text-3xl font-bold">{stats.qualifiedLeads}</p>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold">Commissions Totales</h3>
                  <DollarSign className="w-6 h-6 text-orange-500" />
                </div>
                <p className="text-3xl font-bold">{stats.earnings.toFixed(2)}€</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-6">Nouvelle Recommandation</h2>
                <form onSubmit={handleAddReferral} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom du prospect
                    </label>
                    <input
                      type="text"
                      name="prospect_name"
                      required
                      className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email du prospect
                    </label>
                    <input
                      type="email"
                      name="prospect_email"
                      required
                      className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Entreprise du prospect
                    </label>
                    <input
                      type="text"
                      name="prospect_company"
                      required
                      className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone du prospect
                    </label>
                    <input
                      type="tel"
                      name="prospect_phone"
                      className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes / Commentaires
                    </label>
                    <textarea
                      name="notes"
                      rows={3}
                      className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-orange-600 transition-colors"
                  >
                    {loading ? 'Envoi...' : 'Ajouter la recommandation'}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </form>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-6">Vos Recommandations</h2>
                <div className="space-y-6">
                  {referrals.map((referral: any) => (
                    <div key={referral.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-bold">{referral.prospect_name}</h3>
                          <p className="text-sm text-gray-500">{referral.prospect_company}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(referral.status)}`}>
                          {getStatusText(referral.status)}
                        </span>
                      </div>
                      <div className="flex items-center gap-8 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <MessageCircle className="w-4 h-4" />
                          <span>{referral.notes || 'Aucune note'}</span>
                        </div>
                        {referral.referral_commissions?.[0] && (
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            <span>{referral.referral_commissions[0].commission_amount}€</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {referrals.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Vous n'avez pas encore de recommandations.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}